import { Chip } from "@heroui/react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { relativeTime } from "@/src/libs/relativeTime";
import type { AdminAccount } from "@/src/pages/Accounts/accounts.type";
import { Panel } from "@/src/pages/Dashboard/components/Panel";
import { NEW_ACCOUNTS_PREVIEW } from "@/src/pages/Dashboard/hooks/useAccountPulse";
import { ROUTES } from "@/src/routes/paths";

type NewAccountsPanelProps = {
  accounts: AdminAccount[];
  isLoading: boolean;
  isError: boolean;
};

/**
 * Les derniers comptes créés.
 *
 * ⚠️ **Pas d'e-mail ici**, contrairement à la liste des comptes : on vient y
 * lire un rythme d'arrivées, pas identifier quelqu'un. L'adresse est la donnée
 * la plus sensible que l'admin manipule ; elle reste sur l'écran qu'on ouvre
 * pour elle.
 *
 * La pastille « non vérifié » y figure quand même : une vague d'inscriptions
 * qui ne confirment jamais leur adresse est le premier signe d'un robot.
 */
export function NewAccountsPanel({
  accounts,
  isLoading,
  isError,
}: NewAccountsPanelProps) {
  const t = useTranslations("dashboard");
  const ta = useTranslations("accounts");
  const { locale } = useLocale();

  const now = Date.now();

  return (
    <Panel
      emptyLabel={t("accounts-empty")}
      errorLabel={t("panel-error")}
      href={ROUTES.accounts}
      isEmpty={accounts.length === 0}
      isError={isError}
      isLoading={isLoading}
      rows={NEW_ACCOUNTS_PREVIEW}
      seeAll={t("see-all")}
      seeAllLabel={t("see-all-accounts")}
      title={t("accounts-title")}
    >
      {accounts.map((account) => (
        <li
          className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-sm"
          key={account.id}
        >
          <AccountBadge account={account} isLinked />

          {account.verified ? null : (
            <Chip size="sm" variant="secondary">
              {ta("unverified")}
            </Chip>
          )}

          <span className="ms-auto shrink-0 text-xs text-muted tabular-nums">
            {relativeTime(account.createdAt, locale, now)}
          </span>
        </li>
      ))}
    </Panel>
  );
}
